import { useCallback, useEffect, useMemo, useState } from 'react';
import { discountsApi, productsApi } from '@/api';
import type { CouponDto, CouponRequest, DiscountDto, DiscountRequest } from '@/api/discounts.api';
import { ProductSelect } from '@/components/selects';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { DateRangeFields } from '@/components/ui/DateRangeFields';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import type { ProductResponse } from '@/types/product';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

type Tab = 'offers' | 'coupons' | 'products';
type DiscountType = 'PERCENTAGE' | 'FLAT';

const DISCOUNT_TYPE_OPTIONS = [
  { value: 'PERCENTAGE', label: 'Percentage (%)' },
  { value: 'FLAT', label: 'Flat amount' },
];

function formatDiscountType(type: string) {
  return type === 'PERCENTAGE' ? 'Percentage' : type === 'FLAT' ? 'Flat amount' : type;
}

function formatDiscountValue(type: string, value: number) {
  return type === 'PERCENTAGE' ? `${value}%` : `₹${value}`;
}

const emptyOfferForm = () => ({
  name: '',
  discountType: 'PERCENTAGE' as DiscountType,
  value: '',
  minCartAmount: '',
  startDate: '',
  endDate: '',
  festivalOffer: false,
  productId: '' as number | '',
});

const emptyCouponForm = () => ({
  code: '',
  discountType: 'PERCENTAGE' as DiscountType,
  value: '',
  maxUses: '',
  startDate: '',
  endDate: '',
});

export function DiscountsPage() {
  const [tab, setTab] = useState<Tab>('offers');
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [discounts, setDiscounts] = useState<DiscountDto[]>([]);
  const [coupons, setCoupons] = useState<CouponDto[]>([]);
  const [products, setProducts] = useState<ProductResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [editDiscount, setEditDiscount] = useState<DiscountDto | null>(null);
  const [editCoupon, setEditCoupon] = useState<CouponDto | null>(null);
  const [editProduct, setEditProduct] = useState<ProductResponse | null>(null);
  const [offerForm, setOfferForm] = useState(emptyOfferForm);
  const [couponForm, setCouponForm] = useState(emptyCouponForm);
  const [discountPct, setDiscountPct] = useState('');
  const [saving, setSaving] = useState(false);

  const offerValueLabel = useMemo(
    () => (offerForm.discountType === 'PERCENTAGE' ? 'Discount percentage *' : 'Flat discount amount *'),
    [offerForm.discountType],
  );
  const couponValueLabel = useMemo(
    () => (couponForm.discountType === 'PERCENTAGE' ? 'Discount percentage *' : 'Flat discount amount *'),
    [couponForm.discountType],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'offers') {
        const res = await discountsApi.list({
          page: pagination.page,
          size: pagination.size,
          search: debouncedSearch || undefined,
        });
        setDiscounts(res.content);
        pagination.applyPageResponse(res);
      } else if (tab === 'coupons') {
        const res = await discountsApi.listCoupons({
          page: pagination.page,
          size: pagination.size,
          search: debouncedSearch || undefined,
        });
        setCoupons(res.content);
        pagination.applyPageResponse(res);
      } else {
        const res = await productsApi.list({
          page: pagination.page,
          size: pagination.size,
          search: debouncedSearch || undefined,
          activeOnly: true,
        });
        setProducts(res.content);
        pagination.applyPageResponse(res);
      }
    } catch (err) {
      showApiError(err, 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [tab, pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const openNewOffer = () => {
    setEditDiscount(null);
    setOfferForm(emptyOfferForm());
    setOfferModalOpen(true);
  };

  const openEditOffer = (r: DiscountDto) => {
    setEditDiscount(r);
    setOfferForm({
      name: r.name,
      discountType: (r.discountType === 'FLAT' ? 'FLAT' : 'PERCENTAGE') as DiscountType,
      value: String(r.value),
      minCartAmount: r.minCartAmount != null ? String(r.minCartAmount) : '',
      startDate: r.startDate?.slice(0, 10) ?? '',
      endDate: r.endDate?.slice(0, 10) ?? '',
      festivalOffer: !!r.festivalOffer,
      productId: r.productId ?? '',
    });
    setOfferModalOpen(true);
  };

  const openNewCoupon = () => {
    setEditCoupon(null);
    setCouponForm(emptyCouponForm());
    setCouponModalOpen(true);
  };

  const openEditCoupon = (r: CouponDto) => {
    setEditCoupon(r);
    setCouponForm({
      code: r.code,
      discountType: (r.discountType === 'FLAT' ? 'FLAT' : 'PERCENTAGE') as DiscountType,
      value: String(r.value),
      maxUses: r.maxUses != null ? String(r.maxUses) : '',
      startDate: r.startDate?.slice(0, 10) ?? '',
      endDate: r.endDate?.slice(0, 10) ?? '',
    });
    setCouponModalOpen(true);
  };

  const saveOffer = async () => {
    if (!offerForm.name.trim()) {
      notify.error('Offer name is required');
      return;
    }
    const value = Number(offerForm.value);
    if (!Number.isFinite(value) || value <= 0) {
      notify.error('Enter a valid discount value');
      return;
    }
    if (offerForm.discountType === 'PERCENTAGE' && value > 100) {
      notify.error('Percentage cannot exceed 100');
      return;
    }
    setSaving(true);
    try {
      const payload: DiscountRequest = {
        name: offerForm.name.trim(),
        discountType: offerForm.discountType,
        value,
        festivalOffer: offerForm.festivalOffer,
        minCartAmount: offerForm.minCartAmount ? Number(offerForm.minCartAmount) : undefined,
        startDate: offerForm.startDate || undefined,
        endDate: offerForm.endDate || undefined,
        productId: offerForm.productId ? Number(offerForm.productId) : undefined,
      };
      if (editDiscount) {
        await discountsApi.update(editDiscount.uuid, payload);
      } else {
        await discountsApi.create(payload);
      }
      notify.success('Offer saved');
      setOfferModalOpen(false);
      load();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveCoupon = async () => {
    if (!couponForm.code.trim()) {
      notify.error('Coupon code is required');
      return;
    }
    const value = Number(couponForm.value);
    if (!Number.isFinite(value) || value <= 0) {
      notify.error('Enter a valid discount value');
      return;
    }
    if (couponForm.discountType === 'PERCENTAGE' && value > 100) {
      notify.error('Percentage cannot exceed 100');
      return;
    }
    setSaving(true);
    try {
      const payload: CouponRequest = {
        code: couponForm.code.trim().toUpperCase(),
        discountType: couponForm.discountType,
        value,
        maxUses: couponForm.maxUses ? Number(couponForm.maxUses) : undefined,
        startDate: couponForm.startDate || undefined,
        endDate: couponForm.endDate || undefined,
      };
      if (editCoupon) {
        await discountsApi.updateCoupon(editCoupon.uuid, payload);
      } else {
        await discountsApi.createCoupon(payload);
      }
      notify.success('Coupon saved');
      setCouponModalOpen(false);
      load();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const saveProductDiscount = async () => {
    if (!editProduct) return;
    const pct = Number(discountPct);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      notify.error('Enter a percentage between 0 and 100');
      return;
    }
    setSaving(true);
    try {
      await productsApi.update(editProduct.id, {
        sku: editProduct.sku,
        name: editProduct.name,
        sellingPrice: editProduct.sellingPrice,
        discountPercentage: pct,
        taxPercentage: editProduct.taxPercentage,
        barcode: editProduct.barcode,
        categoryId: editProduct.categoryId,
        brandId: editProduct.brandId,
      });
      notify.success('Product discount updated');
      setEditProduct(null);
      load();
    } catch (err) {
      showApiError(err, 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const discountColumns: Column<DiscountDto>[] = [
    { key: 'name', header: 'Offer name' },
    {
      key: 'discountType',
      header: 'Discount type',
      render: (r) => formatDiscountType(r.discountType),
    },
    {
      key: 'value',
      header: 'Value',
      render: (r) => formatDiscountValue(r.discountType, r.value),
    },
    {
      key: 'festivalOffer',
      header: 'Festival offer',
      render: (r) => (r.festivalOffer ? 'Yes' : 'No'),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <button type="button" className="text-primary-600" onClick={() => openEditOffer(r)}>
          Edit
        </button>
      ),
    },
  ];

  const couponColumns: Column<CouponDto>[] = [
    { key: 'code', header: 'Coupon code' },
    {
      key: 'discountType',
      header: 'Discount type',
      render: (r) => formatDiscountType(r.discountType),
    },
    {
      key: 'value',
      header: 'Value',
      render: (r) => formatDiscountValue(r.discountType, r.value),
    },
    { key: 'usedCount', header: 'Times used' },
    { key: 'maxUses', header: 'Max uses' },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <button type="button" className="text-primary-600" onClick={() => openEditCoupon(r)}>
          Edit
        </button>
      ),
    },
  ];

  const productColumns: Column<ProductResponse>[] = [
    { key: 'sku', header: 'SKU' },
    { key: 'name', header: 'Product name' },
    {
      key: 'discountPercentage',
      header: 'Catalog discount %',
      render: (r) => `${r.discountPercentage ?? 0}%`,
    },
    {
      key: 'edit',
      header: '',
      render: (r) => (
        <button
          type="button"
          className="text-primary-600"
          onClick={() => {
            setEditProduct(r);
            setDiscountPct(String(r.discountPercentage ?? 0));
          }}
        >
          Edit
        </button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Discounts & Offers"
        description="Manage offers, coupon codes, and product-level discounts"
        breadcrumbs={[{ label: 'POS', to: '/pos/dashboard' }, { label: 'Discounts' }]}
        actions={
          tab === 'offers' ? (
            <Button onClick={openNewOffer}>Add offer</Button>
          ) : tab === 'coupons' ? (
            <Button onClick={openNewCoupon}>Add coupon</Button>
          ) : undefined
        }
      />
      <div className="mb-4 flex gap-2">
        {(['offers', 'coupons', 'products'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              tab === t ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
            }`}
            onClick={() => {
              setTab(t);
              pagination.resetPage();
            }}
          >
            {t === 'offers' ? 'Offers' : t === 'coupons' ? 'Coupons' : 'Product %'}
          </button>
        ))}
      </div>

      {tab === 'offers' && (
        <DataTable columns={discountColumns}
          data={discounts}
          keyField="id"
          loading={loading}
          search={pagination.search}
          onSearchChange={(v) => {
            pagination.setSearch(v);
            pagination.resetPage();
          }}
          page={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={pagination.setPage}
        />
      )}
      {tab === 'coupons' && (
        <DataTable columns={couponColumns}
          data={coupons}
          keyField="id"
          loading={loading}
          search={pagination.search}
          onSearchChange={(v) => {
            pagination.setSearch(v);
            pagination.resetPage();
          }}
          page={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={pagination.setPage}
        />
      )}
      {tab === 'products' && (
        <DataTable columns={productColumns}
          data={products}
          keyField="id"
          loading={loading}
          search={pagination.search}
          onSearchChange={(v) => {
            pagination.setSearch(v);
            pagination.resetPage();
          }}
          page={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={pagination.setPage}
        />
      )}

      <Modal
        open={offerModalOpen}
        onClose={() => setOfferModalOpen(false)}
        title={editDiscount ? 'Edit offer' : 'New offer'}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOfferModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveOffer} loading={saving}>
              Save offer
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Offer name *"
            value={offerForm.name}
            onChange={(e) => setOfferForm({ ...offerForm, name: e.target.value })}
            placeholder="e.g. Weekend sale"
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Discount type *"
              options={DISCOUNT_TYPE_OPTIONS}
              value={offerForm.discountType}
              onChange={(v) =>
                setOfferForm({ ...offerForm, discountType: v as DiscountType })
              }
            />
            <Input
              label={offerValueLabel}
              type="number"
              min={0}
              max={offerForm.discountType === 'PERCENTAGE' ? 100 : undefined}
              step="any"
              value={offerForm.value}
              onChange={(e) => setOfferForm({ ...offerForm, value: e.target.value })}
              placeholder={offerForm.discountType === 'PERCENTAGE' ? 'e.g. 10' : 'e.g. 50'}
            />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {offerForm.discountType === 'PERCENTAGE'
              ? 'Percentage is taken off the eligible cart subtotal.'
              : 'Flat amount is subtracted from the cart subtotal (capped at subtotal).'}
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Minimum cart amount"
              type="number"
              min={0}
              step="any"
              value={offerForm.minCartAmount}
              onChange={(e) => setOfferForm({ ...offerForm, minCartAmount: e.target.value })}
              placeholder="Optional"
            />
            <div className="sm:col-span-2">
              <ProductSelect
                label="Specific product (optional)"
                value={offerForm.productId ? String(offerForm.productId) : ''}
                onChange={(productId) => setOfferForm({ ...offerForm, productId: productId ? Number(productId) : '' })}
                placeholder="All products if empty"
              />
            </div>
          </div>
          <DateRangeFields
            from={offerForm.startDate}
            to={offerForm.endDate}
            onFromChange={(v) => setOfferForm({ ...offerForm, startDate: v })}
            onToChange={(v) => setOfferForm({ ...offerForm, endDate: v })}
            fromLabel="Valid from"
            toLabel="Valid until"
          />
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5 dark:border-slate-600">
            <input
              type="checkbox"
              checked={offerForm.festivalOffer}
              onChange={(e) => setOfferForm({ ...offerForm, festivalOffer: e.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-primary-600"
            />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Mark as festival / seasonal offer
            </span>
          </label>
        </div>
      </Modal>

      <Modal
        open={couponModalOpen}
        onClose={() => setCouponModalOpen(false)}
        title={editCoupon ? 'Edit coupon' : 'New coupon'}
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCouponModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveCoupon} loading={saving}>
              Save coupon
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Coupon code *"
            value={couponForm.code}
            onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
            placeholder="e.g. SAVE10"
            autoComplete="off"
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label="Discount type *"
              options={DISCOUNT_TYPE_OPTIONS}
              value={couponForm.discountType}
              onChange={(v) =>
                setCouponForm({ ...couponForm, discountType: v as DiscountType })
              }
            />
            <Input
              label={couponValueLabel}
              type="number"
              min={0}
              max={couponForm.discountType === 'PERCENTAGE' ? 100 : undefined}
              step="any"
              value={couponForm.value}
              onChange={(e) => setCouponForm({ ...couponForm, value: e.target.value })}
              placeholder={couponForm.discountType === 'PERCENTAGE' ? 'e.g. 10' : 'e.g. 100'}
            />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Used at POS checkout when the cashier enters this coupon code.
          </p>
          <Input
            label="Maximum uses"
            type="number"
            min={1}
            step={1}
            value={couponForm.maxUses}
            onChange={(e) => setCouponForm({ ...couponForm, maxUses: e.target.value })}
            placeholder="Leave empty for unlimited"
          />
          <DateRangeFields
            from={couponForm.startDate}
            to={couponForm.endDate}
            onFromChange={(v) => setCouponForm({ ...couponForm, startDate: v })}
            onToChange={(v) => setCouponForm({ ...couponForm, endDate: v })}
            fromLabel="Valid from"
            toLabel="Valid until"
          />
        </div>
      </Modal>

      <Modal
        open={!!editProduct}
        onClose={() => setEditProduct(null)}
        title="Product catalog discount"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditProduct(null)}>
              Cancel
            </Button>
            <Button onClick={saveProductDiscount} loading={saving}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <span className="label">Product</span>
            <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100">
              {editProduct?.name}
              {editProduct?.sku ? ` · ${editProduct.sku}` : ''}
            </p>
          </div>
          <Input
            label="Discount percentage *"
            type="number"
            min={0}
            max={100}
            step="any"
            value={discountPct}
            onChange={(e) => setDiscountPct(e.target.value)}
            placeholder="0–100"
          />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Default discount applied to this product in the catalog (separate from offers and coupons).
          </p>
        </div>
      </Modal>
    </div>
  );
}
